/**
 * API'ye gönderilecek FavoritedHotel payload'ını oluşturur.
 * Search, Landing ve Detail sayfalarından farklı veri yapılarıyla çağrılır.
 * Eksik alanlar null veya varsayılan değerlerle doldurulur.
 *
 * API beklediği yapı (PascalCase):
 * HotelId, HotelSlug, HotelName, AdultCount, ChildCount, ChildrenAges,
 * Language, Currency, Price, CheckinDate, CheckoutDate,
 * Amenities, StarNumber, SurveyScore, BoardName, LocationName, Images
 */

/**
 * ISO tarih formatına çevirir
 */
function formatDateForApi(date) {
  if (!date) return null;
  if (date instanceof Date) {
    return date.toISOString();
  }
  if (typeof date === "string") {
    if (date.includes("T")) return date;
    const dateObj = new Date(date);
    return Number.isNaN(dateObj.getTime()) ? null : dateObj.toISOString();
  }
  return null;
}

/**
 * Amenity objesini API formatına çevirir
 */
function mapAmenityToApi(amenity) {
  if (!amenity || typeof amenity !== "object") return null;
  return {
    AmenityId: String(amenity.AmenityId ?? amenity.Code ?? amenity.id ?? ""),
    AmenityName: amenity.AmenityName ?? amenity.Name ?? "",
    Category: amenity.Category ?? null,
    CategoryId: amenity.CategoryId ?? null,
    Slug: amenity.Slug ?? null,
    Highlighted: amenity.Highlighted ?? false,
    Sort: amenity.Sort ?? 0,
    Paid: amenity.Paid ?? false,
    Limited: amenity.Limited ?? false,
    Code: amenity.Code ?? null,
  };
}

/**
 * Genel FavoritedHotel payload builder
 * @param {Object} params - Alanlar
 */
export function buildFavoritedHotelPayload({
  hotelId,
  hotelSlug = "",
  hotelName = "",
  locationName = "",
  adultCount = 1,
  childCount = 0,
  childrenAges = null,
  language = "tr",
  currency = "TRY",
  price = null,
  checkinDate = null,
  checkoutDate = null,
  amenities = null,
  starNumber = null,
  surveyScore = null,
  boardName = null,
  images = null,
} = {}) {
  const amenityList = Array.isArray(amenities)
    ? amenities.map(mapAmenityToApi).filter(Boolean)
    : [];
  const imageList = Array.isArray(images) ? images.slice(0, 5) : [];

  return {
    HotelId: hotelId,
    HotelSlug: hotelSlug ?? "",
    HotelName: hotelName ?? "",
    AdultCount: adultCount ?? 0,
    ChildCount: childCount ?? 0,
    ChildrenAges: Array.isArray(childrenAges) ? childrenAges : [],
    Language: language ?? "tr",
    Currency: currency ?? "TRY",
    Price: price != null ? String(price) : "0",
    CheckinDate: formatDateForApi(checkinDate) ?? "",
    CheckoutDate: formatDateForApi(checkoutDate) ?? "",
    Amenities: amenityList,
    StarNumber: starNumber ?? "",
    SurveyScore: surveyScore ?? 0.0,
    BoardName: boardName ?? "",
    LocationName: locationName ?? "",
    Images: imageList,
  };
}

/**
 * Search sayfası hotelData'sından payload oluşturur
 * @param {Object} hotelData - SearchCardItem'dan gelen normalize edilmiş otel
 * @param {Object} context - { pathname, searchParams, locale, currency }
 */
export function buildFromSearchData(hotelData, context = {}) {
  const { pathname = "", searchParams = {}, locale = "tr", currency = "TRY" } = context;

  let checkinDate = null;
  let checkoutDate = null;
  const pathParts = pathname.split("/").filter(Boolean);
  const searchIndex = pathParts.findIndex((p) => p === "search" || p === "ara");
  if (searchIndex !== -1) {
    const ci = pathParts[searchIndex + 2];
    const co = pathParts[searchIndex + 3];
    if (ci) checkinDate = new Date(ci);
    if (co) checkoutDate = new Date(co);
  }

  const adult = parseInt(searchParams.adult || searchParams.adult_1 || "1", 10);
  const child = parseInt(searchParams.child || searchParams.child_1 || "0", 10);
  let childrenAges = null;
  const childAges = searchParams.childAges;
  if (childAges && child > 0) {
    const decoded = decodeURIComponent(childAges);
    childrenAges = decoded
      .split(",")
      .filter(Boolean)
      .map((a) => parseInt(a.trim(), 10))
      .filter((a) => !Number.isNaN(a));
    if (childrenAges.length === 0) childrenAges = null;
  }

  let price = null;
  if (hotelData?.price != null) {
    const priceStr =
      typeof hotelData.price === "string"
        ? hotelData.price.replace(/[^\d,.]/g, "").replace(",", ".")
        : String(hotelData.price);
    price = parseFloat(priceStr) || null;
  }

  let hotelSlug = hotelData?.link
    ? hotelData.link.split("/").filter(Boolean).pop() || ""
    : "";

  return buildFavoritedHotelPayload({
    hotelId: hotelData?.id ?? hotelData?.hotelId,
    hotelSlug,
    hotelName: hotelData?.name || "",
    locationName: hotelData?.location || "",
    adultCount: adult,
    childCount: child,
    childrenAges,
    language: locale,
    currency,
    price,
    checkinDate,
    checkoutDate,
    amenities: hotelData?.amenities,
    starNumber: hotelData?.starRating,
    surveyScore: hotelData?.rating,
    boardName: hotelData?.breakfast,
    images: hotelData?.imageUrl,
  });
}

/**
 * Landing sayfası hotelData'sından payload oluşturur
 */
export function buildFromLandingData(hotelData, context = {}) {
  const { locale = "tr", currency = "TRY" } = context;

  let hotelSlug = hotelData?.hotelSlug || "";
  if (!hotelSlug && hotelData?.link) {
    hotelSlug = hotelData.link.split("/").filter(Boolean).pop() || "";
  }

  const images = hotelData?.imageUrl ?? hotelData?.Images;

  return buildFavoritedHotelPayload({
    hotelId: hotelData?.id,
    hotelSlug,
    hotelName: hotelData?.name || "",
    locationName: hotelData?.location || "",
    adultCount: 1,
    childCount: 0,
    childrenAges: [],
    language: locale,
    currency,
    price: null,
    checkinDate: null,
    checkoutDate: null,
    amenities: hotelData?.amenities,
    starNumber: hotelData?.starRating ?? hotelData?.starNumber,
    surveyScore: hotelData?.rating ?? hotelData?.surveyScore,
    boardName: hotelData?.breakfast ?? hotelData?.boardName,
    images,
  });
}

/**
 * PensionLabel -> BoardName dönüşümü (RO, BB, HB, FB, AI vb.)
 */
function mapPensionLabelToBoardName(pensionLabel) {
  if (!pensionLabel || typeof pensionLabel !== "string") return null;
  const map = {
    RO: "IsRoomOnly",
    BB: "BedAndBreakfast",
    HB: "HalfBoard",
    FB: "FullBoard",
    AI: "AllInclusive",
  };
  return map[pensionLabel.toUpperCase()] ?? pensionLabel;
}

/**
 * RoomTypes/RoomPrices'tan en düşük fiyat ve board bilgisini alır
 */
function getPriceAndBoardFromRooms(roomTypes) {
  let minPrice = null;
  let currency = "TRY";
  let boardName = null;
  if (!Array.isArray(roomTypes) || roomTypes.length === 0) return { price: null, currency, boardName };
  for (const room of roomTypes) {
    const prices = room?.RoomPrices;
    if (!Array.isArray(prices)) continue;
    for (const rp of prices) {
      const netPrice = rp?.RoomPricing?.NetPrice ?? rp?.RoomPricing?.GrossPrice;
      if (netPrice != null && (minPrice == null || netPrice < minPrice)) {
        minPrice = netPrice;
        currency = rp?.RoomPricing?.Currency ?? currency;
        boardName = mapPensionLabelToBoardName(rp?.PensionLabel) ?? boardName;
      }
    }
  }
  return { price: minPrice, currency, boardName };
}

/**
 * Otel detay sayfası hotelData'sından payload oluşturur.
 * İki farklı flow destekler:
 * 1) Basket/Products flow: CheckinDate, CheckoutDate, RoomTypes, AdultCount vb. var
 * 2) Sadece otel detay: Bu alanlar yok, null/varsayılan değerler kullanılır
 *
 * context üzerinden checkinDate, checkoutDate, roomsData, adultCount, childCount, childrenAges geçilebilir
 */
export function buildFromDetailData(hotelData, context = {}) {
  const {
    locale = "tr",
    currency: contextCurrency = "TRY",
    checkinDate: ctxCheckin,
    checkoutDate: ctxCheckout,
    roomsData: ctxRooms,
    adultCount: ctxAdult,
    childCount: ctxChild,
    childrenAges: ctxChildrenAges,
  } = context;

  // RoomTypes: hotelData.RoomTypes veya context.roomsData
  const roomTypes = hotelData?.RoomTypes ?? ctxRooms ?? [];
  const { price, currency, boardName } = getPriceAndBoardFromRooms(roomTypes);
  const hasRoomPrices = price != null;

  // Checkin/Checkout: hotelData veya context
  let checkinDate = null;
  let checkoutDate = null;
  if (hotelData?.CheckinDate) {
    checkinDate = hotelData.CheckinDate instanceof Date ? hotelData.CheckinDate : new Date(hotelData.CheckinDate);
  } else if (ctxCheckin) {
    checkinDate = ctxCheckin instanceof Date ? ctxCheckin : new Date(ctxCheckin);
  }
  if (hotelData?.CheckoutDate) {
    checkoutDate = hotelData.CheckoutDate instanceof Date ? hotelData.CheckoutDate : new Date(hotelData.CheckoutDate);
  } else if (ctxCheckout) {
    checkoutDate = ctxCheckout instanceof Date ? ctxCheckout : new Date(ctxCheckout);
  }

  // HotelDetails veya flat structure
  const details = hotelData?.HotelDetails ?? hotelData;
  const hotelSlug = details?.Slug ?? hotelData?.Slug ?? "";
  const locationName =
    details?.Location ?? details?.City ?? hotelData?.City ?? hotelData?.LocationName ?? "";

  // Images: Medias[].Url veya MainImage
  let images = null;
  const medias = details?.Medias ?? hotelData?.Medias;
  if (Array.isArray(medias) && medias.length > 0) {
    images = medias.map((m) => (typeof m === "string" ? m : m?.Url)).filter(Boolean);
  }
  if ((!images || images.length === 0) && (details?.MainImage ?? hotelData?.MainImage)) {
    images = [details?.MainImage ?? hotelData?.MainImage];
  }

  // Amenities: HotelAmenities (response'ta product seviyesinde)
  const amenities = hotelData?.HotelAmenities ?? details?.HotelAmenities ?? hotelData?.Amenities ?? details?.Amenities;

  return buildFavoritedHotelPayload({
    hotelId: hotelData?.HotelId ?? hotelData?.Id ?? hotelData?.id,
    hotelSlug,
    hotelName: hotelData?.HotelName ?? hotelData?.Name ?? details?.HotelName ?? "",
    locationName,
    adultCount: hasRoomPrices ? (hotelData?.AdultCount ?? ctxAdult ?? 1) : 0,
    childCount: hotelData?.ChildCount ?? ctxChild ?? 0,
    childrenAges: hotelData?.ChildrenAges ?? ctxChildrenAges ?? [],
    language: locale,
    currency: currency || contextCurrency,
    price,
    checkinDate,
    checkoutDate,
    amenities,
    starNumber: details?.StarNumber ?? hotelData?.StarNumber,
    surveyScore: details?.SurveyScore ?? hotelData?.SurveyScore ?? 0,
    boardName: hotelData?.BoardName ?? boardName,
    images,
  });
}
